import { journeyStore } from "@/app/services/journey";
import { PartnerType } from "@/app/utils/partners.enum";
import { cltService } from "@/app/services/cltService";
import {
  AuthorizationRequest,
  AuthorizationResponse,
} from "@/app/services/cltService/requestAuthorization";
import { useCustomerCpf } from "@/app/hooks/useCustomerCpf";
import { validateCpf } from "@/app/utils/cpfFormater";
import masks from "@/app/utils/masks";
import { useToast } from "@/view/components/ui/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useRegisterSupportContext } from "@/app/store/supportScreenContext"

const isValidDate = (value: string) => {
  const [day, month, year] = value.split("/").map(Number);

  if (!day || !month || !year) return false;

  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

const parseDateToISO = (dateString: string) => {
  if (!dateString) return "";

  const [day, month, year] = dateString.split("/");

  if (!day || !month || !year) return "";

  const date = new Date(`${year}-${month}-${day}T03:00:00.000Z`);

  if (isNaN(date.getTime())) return "";

  return date.toISOString();
};

const formatBirthDateValue = (birthDate?: string) => {
  if (!birthDate) return "";

  const [year, month, day] = birthDate.slice(0, 10).split("-");

  if (!year || !month || !day) return "";

  return `${day}/${month}/${year}`;
};

const getAvailableRegisterSchema = z.object({
  cpf: z.string().refine((v) => validateCpf(v || ""), {
    message: "CPF inválido",
  }),
  name: z
    .string()
    .min(1, "Nome é obrigatório")
    .max(150, "O nome do cliente deve ter no máximo 150 caracteres"),
  email: z.string().min(1, "E-mail é obrigatório").email("E-mail inválido"),
  phone: z
    .string()
    .min(1, "Telefone é obrigatório")
    .refine((v) => (v || "").replace(/\D/g, "").length >= 10, {
      message: "Telefone inválido",
    }),
  birthDate: z
    .string()
    .min(1, "Data de nascimento é obrigatória")
    .refine((v) => isValidDate(v || ""), {
      message: "Data inválida",
    }),
});

type getAvailableRegisterFormData = z.infer<typeof getAvailableRegisterSchema>;

export default function useNewSimulationModal() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [list, setList] = useState<AuthorizationResponse[] | undefined>(
    undefined,
  );
  const [searched, setSearched] = useState(false);
  const [selectedCard, setSelectedCard] = useState<
    AuthorizationResponse | undefined
  >(undefined);

  const form = useForm<getAvailableRegisterFormData>({
    resolver: zodResolver(getAvailableRegisterSchema),
    mode: "onChange",
  });

  const { watch, setValue } = form;

  const cpf = watch("cpf");
  const cleanCpf = cpf?.replace(/\D/g, "");

  const {
    data: customer,
    isSuccess,
    isFetching: isFetchingCustomer,
  } = useCustomerCpf(validateCpf(cleanCpf || "") ? cleanCpf : undefined);

  useEffect(() => {
    if (!isSuccess || !customer) return;

    const setField = (
      field: "name" | "email" | "phone" | "birthDate",
      value?: string,
    ) => {
      if (value) {
        setValue(field, value, { shouldValidate: true });
      }
    };

    setField("name", customer.name);
    setField("email", customer.email);
    setField("phone", masks.phone(customer.phone));
    setField("birthDate", formatBirthDateValue(customer.birthDate));
  }, [isSuccess, customer, setValue]);

  useEffect(() => {
    setList(undefined);
    setSearched(false);
    setSelectedCard(undefined);
  }, [cleanCpf]);

  const goToOffers = (cpf: string, matricula?: string) => {
    if (!cpf) return;
    const query = matricula
      ? `?matricula=${encodeURIComponent(matricula)}`
      : "";
    navigate(`/transacoes/${masks.number(cpf)}/clt${query}`);
  };

  const { mutateAsync: requestAuth, isPending } = useMutation({
    mutationFn: (payload: AuthorizationRequest) =>
      cltService.requestAuthorization(payload),
    onSuccess: (data, payload) => {
      const eligible = data?.filter((item) => item.Elegivel) || [];
      setList(eligible);
      setSearched(true);

      if (eligible.length === 1) {
        setSelectedCard(eligible[0]);
        goToOffers(payload.cpf, eligible[0].RegistrationNumber);
      }
    },
    onError: (error: unknown) => {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      setList([]);
      setSearched(true);
      toast({
        title: "Erro ao buscar matrículas",
        description:
          err?.response?.data?.message ||
          err?.message ||
          "Erro inesperado. Tente novamente mais tarde.",
        variant: "destructive",
      });
    },
  });

  const needsSelection = !!list && list.length > 1;

  const handleSubmit = form.handleSubmit(async (data) => {
    if (isPending) return;

    if (needsSelection) {
      if (!selectedCard) return;

      goToOffers(data.cpf, selectedCard.RegistrationNumber);
      return;
    }

    if (!validateCpf(data.cpf.replace(/\D/g, ""))) {
      setList([]);
      setSearched(true);
      return;
    }

    try {
      setSelectedCard(undefined);
      journeyStore.start(PartnerType.CLT);

      await requestAuth({
        cpf: masks.cpf(data.cpf),
        name: data.name,
        email: data.email,
        phone: data.phone.replace(/\D/g, ""),
        birthDate: parseDateToISO(data.birthDate),
      });
    } catch (error) {
      console.log(error);
    }
  });

  useRegisterSupportContext("simulation.clt.search", {
    product: PartnerType.CLT,
    step: "Consulta de vínculos CLT",
    data: { ...form.watch(), selectedCard },
  });

  return {
    form,
    isPending,
    isFetchingCustomer,
    handleSubmit,
    list,
    searched,
    selectedCard,
    setSelectedCard,
    needsSelection,
  };
}
